import React, { Suspense, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { usePageTitle } from '../../lib/hooks';
import { z } from 'zod';
import {
 Smartphone,
 Download,
 Copy,
 Check,
 ChevronRight,
 ArrowLeft,
 Settings,
 ShieldCheck,
} from 'lucide-react';
import { Skeleton } from '../../components/ui/Skeleton';

import { AuthLayout } from '../../components/layout/AuthLayout';
import { Button } from '../../components/ui/Button';
import { Stack } from '../../components/layout/Stack';
import { useToast } from '../../components/ui/Toast';

const LazyQRCode = React.lazy(() => import('react-qr-code'));

const appsSearchValidator = z.object({
 app: z.enum(['agent', 'customer']).optional(),
});

export const Route = createFileRoute('/_public/apps')({
 validateSearch: (search) => appsSearchValidator.parse(search),
 component: AppsPage,
});

function AppsPage() {
 usePageTitle('Mobile Apps');
 const { app } = Route.useSearch();
 const navigate = useNavigate({ from: '/apps' });
 const { toast } = useToast();
 const [copied, setCopied] = useState(false);

 const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

 const handleCopyLink = async () => {
 try {
 await navigator.clipboard.writeText(currentUrl);
 setCopied(true);
 toast({
 type: 'success',
 title: 'Link Copied',
 description: 'Shareable link has been copied to your clipboard.',
 });
 setTimeout(() => setCopied(false), 2000);
 } catch (err) {
 console.error('Failed to copy link to clipboard:', err);
 toast({
 type: 'error',
 title: 'Failed to copy',
 description: 'Could not copy link to clipboard.',
 });
 }
 };

  // 1. Selector Mode (No specific app parameter)
  if (!app) {
    return (
      <AuthLayout 
        title="Download Mobile Apps" 
        subtitle="Access Kraphene platform features directly on your mobile device."
        cardClassName="max-w-[500px]"
      >
        <Stack gap="5" className="w-full">
          <div
            role="button"
            tabIndex={0}
            onClick={() => void navigate({ to: '/apps', search: { app: 'agent' } })}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                void navigate({ to: '/apps', search: { app: 'agent' } });
              }
            }}
            className="w-full text-left p-5 rounded-radius-lg border border-border bg-surface-1 hover:border-accent-500 hover:bg-surface-2 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500 focus-visible:ring-offset-2 transition-all group flex items-start justify-between cursor-pointer"
          >
            <div className="flex items-start gap-4 mr-2">
              <div className="p-3 bg-accent-50 text-accent-500 rounded-radius-md group-hover:bg-accent-100 transition-colors flex-shrink-0">
                <Smartphone className="w-6 h-6" />
              </div>
              <div className="space-y-2">
                <div>
                  <span className="text-[10px] font-bold tracking-wider text-accent-500 uppercase">Field Operations</span>
                  <h3 className="text-base font-semibold text-text-primary mt-0.5">Agent Mobile App</h3>
                </div>
                <p className="text-xs text-text-secondary leading-relaxed">
                  For collection agents and field operators to manage members and record collections.
                </p>
                <div className="flex flex-col gap-1.5 pt-1">
                  <div className="flex items-center gap-2 text-caption text-text-muted">
                    <span className="w-1.5 h-1.5 rounded-full bg-accent-400"></span>
                    <span>Real-time Cash Collection</span>
                  </div>
                  <div className="flex items-center gap-2 text-caption text-text-muted">
                    <span className="w-1.5 h-1.5 rounded-full bg-accent-400"></span>
                    <span>Digital Deposit Receipts</span>
                  </div>
                  <div className="flex items-center gap-2 text-caption text-text-muted">
                    <span className="w-1.5 h-1.5 rounded-full bg-accent-400"></span>
                    <span>Member Verification</span>
                  </div>
                </div>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-text-muted group-hover:text-text-primary group-hover:translate-x-0.5 transition-all flex-shrink-0 self-center" />
          </div>

          <div
            role="button"
            tabIndex={0}
            onClick={() => void navigate({ to: '/apps', search: { app: 'customer' } })}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                void navigate({ to: '/apps', search: { app: 'customer' } });
              }
            }}
            className="w-full text-left p-5 rounded-radius-lg border border-border bg-surface-1 hover:border-accent-500 hover:bg-surface-2 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500 focus-visible:ring-offset-2 transition-all group flex items-start justify-between cursor-pointer"
          >
            <div className="flex items-start gap-4 mr-2">
              <div className="p-3 bg-accent-50 text-accent-500 rounded-radius-md group-hover:bg-accent-100 transition-colors flex-shrink-0">
                <Smartphone className="w-6 h-6" />
              </div>
              <div className="space-y-2">
                <div>
                  <span className="text-[10px] font-bold tracking-wider text-accent-500 uppercase">Subscribers</span>
                  <h3 className="text-base font-semibold text-text-primary mt-0.5">Customer Mobile App</h3>
                </div>
                <p className="text-xs text-text-secondary leading-relaxed">
                  For subscribers and members to view active chits, monitor ledger balances, and pay installments.
                </p>
                <div className="flex flex-col gap-1.5 pt-1">
                  <div className="flex items-center gap-2 text-caption text-text-muted">
                    <span className="w-1.5 h-1.5 rounded-full bg-accent-400"></span>
                    <span>Chit Group Overview</span>
                  </div>
                  <div className="flex items-center gap-2 text-caption text-text-muted">
                    <span className="w-1.5 h-1.5 rounded-full bg-accent-400"></span>
                    <span>Digital Receipts & History</span>
                  </div>
                  <div className="flex items-center gap-2 text-caption text-text-muted">
                    <span className="w-1.5 h-1.5 rounded-full bg-accent-400"></span>
                    <span>Instant Online Payments</span>
                  </div>
                </div>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-text-muted group-hover:text-text-primary group-hover:translate-x-0.5 transition-all flex-shrink-0 self-center" />
          </div>
        </Stack>
      </AuthLayout>
    );
  }

  // 2. Targeted Mode (app === 'agent' or 'customer')
  const isAgent = app === 'agent';
  const appTitle = isAgent ? 'Agent Mobile App' : 'Customer Mobile App';
  const appSubtitle = isAgent 
    ? 'Designed for collection agents and field operators to manage collections on the go.'
    : 'Designed for subscribers to view active chits, pay installments, and check receipts.';
  const downloadUrl = isAgent ? '/downloads/agent-app.apk' : '/downloads/customer-app.apk';

  return (
    <AuthLayout 
      title={appTitle} 
      subtitle={appSubtitle}
      cardClassName="max-w-[540px]" // Wider card to accommodate QR Code beautifully
    >
      <Stack gap="6" className="w-full">
        {/* Back Link */}
        <Button
          variant="ghost"
          size="sm"
          type="button"
          onClick={() => void navigate({ to: '/apps', search: { app: undefined } })}
          className="flex items-center gap-1.5 text-xs font-semibold text-text-secondary hover:text-text-primary transition-colors w-fit"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back to Apps Selection
        </Button>

        {/* Action Card */}
        <div className="bg-surface-2 border border-border p-5 rounded-radius-lg flex flex-col gap-5 md:flex-row md:gap-4">
          <div className="flex flex-col flex-1 gap-4 justify-between">
            <div className="space-y-1">
              <span className="text-caption uppercase font-bold tracking-wider text-accent-500">Android Application Package</span>
              <h4 className="text-sm font-bold text-text-primary">Download Production APK</h4>
              <p className="text-xs text-text-secondary">Direct download to sideload onto any Android device.</p>
            </div>
            <div className="flex flex-col gap-2">
              <a href={downloadUrl} download className="w-full">
                <Button variant="primary" size="md" className="w-full">
                  <Download className="w-4 h-4" />
                  Download APK
                </Button>
              </a>
              <Button
                variant="secondary"
                size="md"
                onClick={handleCopyLink}
                className="w-full"
              >
                {copied ? <Check className="w-4 h-4 text-success-500" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied' : 'Share Link'}
              </Button>
            </div>
          </div>

          {/* QR Code handover */}
          <div className="flex self-center md:self-start flex-shrink-0 flex-col items-center gap-2 bg-surface-1 border border-border p-3.5 rounded-radius-sm">
            {currentUrl && (
              <div className="bg-surface-1 p-1.5 rounded-radius-sm border border-border w-fit">
                <Suspense fallback={<Skeleton className="w-[120px] h-[120px]" />}>
                  <LazyQRCode
                    value={currentUrl}
                    size={120}
                    viewBox="0 0 256 256"
                  />
                </Suspense>
              </div>
            )}
            <span className="text-caption text-text-muted font-medium tracking-wide text-center max-w-[128px]">Scan to download on another phone</span>
          </div>
        </div>

        {/* Installation Instructions */}
        <div className="space-y-3">
          <h4 className="text-xs font-bold uppercase tracking-wider text-text-secondary border-b border-border pb-1.5">
            Android Installation Guide (Sideloading)
          </h4>
          
          <div className="space-y-3">
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-5 h-5 rounded-full bg-accent-50 border border-accent-100 flex items-center justify-center text-caption font-bold text-accent-500">
                1
              </div>
              <div>
                <h5 className="text-xs font-bold text-text-primary flex items-center gap-1.5">
                  <Download className="w-3.5 h-3.5 text-text-muted" />
                  Download APK
                </h5>
                <p className="text-caption text-text-secondary mt-0.5">
                  Click the <b>Download APK</b> button or scan the QR code to fetch the application package onto your Android phone.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="flex-shrink-0 w-5 h-5 rounded-full bg-accent-50 border border-accent-100 flex items-center justify-center text-caption font-bold text-accent-500">
                2
              </div>
              <div>
                <h5 className="text-xs font-bold text-text-primary flex items-center gap-1.5">
                  <Settings className="w-3.5 h-3.5 text-text-muted" />
                  Allow Unknown Sources
                </h5>
                <p className="text-caption text-text-secondary mt-0.5">
                  If prompted by your browser, open your phone's <b>Settings</b> and enable <b>"Allow installation from this source"</b> or <b>"Install Unknown Apps"</b>.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="flex-shrink-0 w-5 h-5 rounded-full bg-accent-50 border border-accent-100 flex items-center justify-center text-caption font-bold text-accent-500">
                3
              </div>
              <div>
                <h5 className="text-xs font-bold text-text-primary flex items-center gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5 text-text-muted" />
                  Complete Installation
                </h5>
                <p className="text-caption text-text-secondary mt-0.5">
                  Open your <b>Downloads</b> folder (or use your file manager), tap on the downloaded file, and click <b>Install</b> to launch the app.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Toggle to Alternative App */}
        <div className="border-t border-border pt-4 flex justify-center items-center text-caption text-text-muted">
          Looking for the other app?&nbsp;
          <Button
            variant="ghost"
            size="sm"
            type="button"
            onClick={() => void navigate({ to: '/apps', search: { app: isAgent ? 'customer' : 'agent' } })}
            className="font-semibold text-accent-500 hover:underline h-auto p-0 min-h-0 bg-transparent border-none"
          >
            Switch to {isAgent ? 'Customer App' : 'Agent App'}
          </Button>
        </div>
      </Stack>
    </AuthLayout>
  );
}
